import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { processMonitoringService } from "../services/processMonitoringService.js";

const ProcessMonitoringContext = createContext(null);

function normalizeCNJ(processNumber) {
  if (!processNumber) return "";
  return String(processNumber).replace(/[\.\-\s]/g, "").trim().toUpperCase();
}

export function ProcessMonitoringProvider({ children }) {
  const { isAuthenticated } = useAuth();

  const [monitorings, setMonitorings] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  const pollingRef = useRef(null);

  const monitoringSet = useMemo(() => {
    const set = new Set();
    for (const m of monitorings || []) {
      if (m?.numberProcess) set.add(normalizeCNJ(m.numberProcess));
    }
    return set;
  }, [monitorings]);

  const fetchMonitorings = useCallback(async () => {
    if (!isAuthenticated) return;
    const res = await processMonitoringService.getMyMonitorings();
    if (res?.success) {
      setMonitorings(res.data || []);
    }
  }, [isAuthenticated]);

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    const res = await processMonitoringService.getUnreadCount();
    if (res?.success) {
      setUnreadCount(Number(res.count || 0));
    }
  }, [isAuthenticated]);

  const fetchNotifications = useCallback(
    async ({ includeRead = false } = {}) => {
      if (!isAuthenticated) return;
      const res = await processMonitoringService.getMyNotifications({ includeRead });
      if (res?.success) {
        setNotifications(res.data || []);
      }
    },
    [isAuthenticated]
  );

  const refreshAll = useCallback(async () => {
    await Promise.allSettled([fetchMonitorings(), fetchUnreadCount()]);
  }, [fetchMonitorings, fetchUnreadCount]);

  const isMonitored = useCallback(
    (processNumber) => {
      const normalized = normalizeCNJ(processNumber);
      if (!normalized) return false;
      return monitoringSet.has(normalized);
    },
    [monitoringSet]
  );

  const toggleMonitoring = useCallback(
    async (processNumber) => {
      if (!isAuthenticated) return { success: false, message: "Não autenticado" };

      const normalized = normalizeCNJ(processNumber);
      if (!normalized) return { success: false, message: "Número do processo inválido" };

      const already = monitoringSet.has(normalized);

      if (already) {
        const res = await processMonitoringService.deleteByProcessNumber(normalized);
        await refreshAll();
        return res;
      } else {
        const res = await processMonitoringService.createByProcessNumber(normalized);
        await refreshAll();
        return res;
      }
    },
    [isAuthenticated, monitoringSet, refreshAll]
  );

  const markNotificationRead = useCallback(
    async (notificationId) => {
      if (!isAuthenticated) return { success: false };
      const res = await processMonitoringService.markNotificationRead(notificationId);
      await Promise.allSettled([fetchUnreadCount(), fetchNotifications({ includeRead: true })]);
      return res;
    },
    [isAuthenticated, fetchUnreadCount, fetchNotifications]
  );

  const markAllNotificationsRead = useCallback(async () => {
    if (!isAuthenticated) return { success: false };
    const res = await processMonitoringService.markAllNotificationsRead();
    await Promise.allSettled([fetchUnreadCount(), fetchNotifications({ includeRead: true })]);
    return res;
  }, [isAuthenticated, fetchUnreadCount, fetchNotifications]);

  // Start/stop polling based on auth
  useEffect(() => {
    if (!isAuthenticated) {
      setMonitorings([]);
      setUnreadCount(0);
      setNotifications([]);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    // Initial load
    refreshAll();

    // Polling (badge + lista) — intervalo longo para reduzir carga no servidor
    pollingRef.current = setInterval(() => {
      refreshAll();
    }, 300000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [isAuthenticated, refreshAll]);

  const value = useMemo(
    () => ({
      monitorings,
      unreadCount,
      notifications,
      refreshAll,
      fetchNotifications,
      isMonitored,
      toggleMonitoring,
      markNotificationRead,
      markAllNotificationsRead,
    }),
    [
      monitorings,
      unreadCount,
      notifications,
      refreshAll,
      fetchNotifications,
      isMonitored,
      toggleMonitoring,
      markNotificationRead,
      markAllNotificationsRead,
    ]
  );

  return (
    <ProcessMonitoringContext.Provider value={value}>
      {children}
    </ProcessMonitoringContext.Provider>
  );
}

export function useProcessMonitoring() {
  const ctx = useContext(ProcessMonitoringContext);
  if (!ctx) {
    return {
      monitorings: [],
      unreadCount: 0,
      notifications: [],
      refreshAll: async () => {},
      fetchNotifications: async () => {},
      isMonitored: () => false,
      toggleMonitoring: async () => ({ success: false }),
      markNotificationRead: async () => ({ success: false }),
      markAllNotificationsRead: async () => ({ success: false }),
    };
  }
  return ctx;
}
